import {
  canonicalTreeEdgeIdentityKey,
  canonicalTreeEdgePairKey,
  type CanonicalTreeEdgeDirection,
  type CanonicalTreeEdgeKind,
  type CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

export interface CanonicalTreeEdgeValidationIssue {
  readonly code: string;
  readonly message: string;
}

export interface CanonicalTreeEdgeValidationResult {
  readonly valid: boolean;
  readonly issues: readonly CanonicalTreeEdgeValidationIssue[];
}

const symmetricKinds = new Set<CanonicalTreeEdgeKind>([
  'sibling',
  'spouse-partner',
  'co-parent',
  'chosen-family',
  'god-family',
]);

/*
 * CC-TREE-LIVE-003G3WJ2
 *
 * Child-mediated means the adult-to-adult edge itself requires a child
 * connector. Affiliated Family is a direct explicit affiliation to a real
 * family anchor and therefore must not manufacture a shared-child requirement.
 * Legacy direct in-law evidence remains child-mediated until its separate
 * affinity-path retirement; the 50-option WJ2 authoring planner does not emit
 * direct in-law facts.
 */
const childMediatedKinds = new Set<CanonicalTreeEdgeKind>([
  'co-parent',
  'in-law',
]);

export const expectedCanonicalTreeEdgeDirection = (
  kind: CanonicalTreeEdgeKind,
): CanonicalTreeEdgeDirection => {
  if (childMediatedKinds.has(kind)) return 'child-mediated';
  if (symmetricKinds.has(kind)) return 'symmetric';
  return 'directed';
};

export const validateCanonicalTreeRelationshipEdge = (
  edge: CanonicalTreeRelationshipEdge,
): CanonicalTreeEdgeValidationResult => {
  const issues: CanonicalTreeEdgeValidationIssue[] = [];

  if (!edge.id.trim()) {
    issues.push({
      code: 'EDGE-ID-REQUIRED',
      message: 'Canonical relationship edge id is required.',
    });
  }

  if (!edge.sourceMemberId.trim() || !edge.targetMemberId.trim()) {
    issues.push({
      code: 'EDGE-ENDPOINTS-REQUIRED',
      message: 'Canonical relationship edges require two member endpoints.',
    });
  }

  if (edge.sourceMemberId === edge.targetMemberId) {
    issues.push({
      code: 'EDGE-NO-SELF-RELATIONSHIP',
      message: 'A member cannot hold a canonical relationship edge to themselves.',
    });
  }

  const expectedDirection =
    expectedCanonicalTreeEdgeDirection(edge.kind);

  if (edge.direction !== expectedDirection) {
    issues.push({
      code: 'EDGE-DIRECTION-MISMATCH',
      message:
        `Relationship kind ${edge.kind} requires direction ${expectedDirection}.`,
    });
  }

  if (
    edge.direction === 'child-mediated' &&
    edge.sharedChildIds.length === 0
  ) {
    issues.push({
      code: 'EDGE-SHARED-CHILD-REQUIRED',
      message:
        `Child-mediated relationship kind ${edge.kind} requires at least one shared child.`,
    });
  }

  if (
    edge.direction !== 'child-mediated' &&
    edge.sharedChildIds.length > 0 &&
    edge.kind !== 'spouse-partner'
  ) {
    issues.push({
      code: 'EDGE-UNEXPECTED-SHARED-CHILD',
      message:
        `Relationship kind ${edge.kind} may not carry shared-child authority.`,
    });
  }

  if (
    edge.rendersActivePartnership &&
    (
      edge.kind !== 'spouse-partner' ||
      edge.status !== 'active'
    )
  ) {
    issues.push({
      code: 'EDGE-ACTIVE-PARTNERSHIP-MISMATCH',
      message:
        'Only an active spouse-partner edge may render an active partnership marker.',
    });
  }

  if (
    edge.createsAncestry &&
    edge.kind !== 'parent-child'
  ) {
    issues.push({
      code: 'EDGE-ANCESTRY-KIND-MISMATCH',
      message:
        'Only parent-child edges may create ancestry.',
    });
  }

  if (
    edge.kind === 'sibling' &&
    (
      edge.createsAncestry ||
      !edge.inheritsGeneration ||
      !edge.inheritsBranch ||
      !edge.inheritsSide
    )
  ) {
    issues.push({
      code: 'EDGE-SIBLING-SEMANTICS-MISMATCH',
      message:
        'Sibling edges must remain lateral and inherit generation, branch, and side without creating ancestry.',
    });
  }

  if (
    edge.kind === 'god-family' &&
    (
      edge.createsAncestry ||
      edge.inheritsBranch ||
      edge.inheritsSide
    )
  ) {
    issues.push({
      code: 'EDGE-GOD-FAMILY-SEMANTICS-MISMATCH',
      message:
        'God-family edges may not create ancestry or inherit bloodline branch or side authority.',
    });
  }

  if (
    edge.kind === 'chosen-family' &&
    edge.createsAncestry
  ) {
    issues.push({
      code: 'EDGE-CHOSEN-FAMILY-ANCESTRY-MISMATCH',
      message:
        'Chosen-family edges do not create ancestry unless represented by a separate parent-child edge.',
    });
  }

  if (
    edge.status === 'active' &&
    edge.endedAt !== null
  ) {
    issues.push({
      code: 'EDGE-ACTIVE-WITH-END-DATE',
      message:
        'An active relationship edge cannot have an end date.',
    });
  }

  if (
    ['divorced', 'ended', 'former', 'historical'].includes(edge.status) &&
    edge.rendersActivePartnership
  ) {
    issues.push({
      code: 'EDGE-HISTORICAL-ACTIVE-MARKER',
      message:
        'Historical relationship edges cannot render an active partnership marker.',
    });
  }

  if (
    new Set(edge.sharedChildIds).size !== edge.sharedChildIds.length
  ) {
    issues.push({
      code: 'EDGE-DUPLICATE-SHARED-CHILD',
      message:
        'Shared child ids must be unique.',
    });
  }

  return Object.freeze({
    valid: issues.length === 0,
    issues: Object.freeze(issues),
  });
};

export const canonicalTreeEdgeGroupKey = (
  edge: CanonicalTreeRelationshipEdge,
): string => {
  const endpoints =
    edge.direction === 'directed'
      ? `${edge.sourceMemberId}->${edge.targetMemberId}`
      : canonicalTreeEdgePairKey(
          edge.sourceMemberId,
          edge.targetMemberId,
        );

  return `${edge.kind}|${endpoints}`;
};

export const canonicalTreeEdgeAuditKey = (
  edge: CanonicalTreeRelationshipEdge,
): string =>
  `${canonicalTreeEdgeGroupKey(edge)}|${canonicalTreeEdgeIdentityKey(edge)}`;
